/* eslint-disable import/prefer-default-export */
import { HIHT_SAMPLING_RATE_DEVICE_MESSAGE, STUDY_STATUS } from '@/shared/config';
import hsIcon from '@/shared/ui/icons/Tables/badge-1k-icon.svg';
import {
  dateToString,
  dateToStringWithDailyTimeZone,
  getStudyDuration,
  isEndOfLife,
  zeroPad,
} from '@/shared/lib';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { DEVICE, checkIcorDevice } from '@/shared/ui';

/**
 * Assembles the study info drawer content as an ordered list of labelled sections. Builds study details (status, start and stop in the facility's daily timezone, device id with iCor, high-sampling-rate and end-of-life badges, duration from initDuration while starting else from start and stop, artifact percentage), an optional follow-on tracking section, patient info, notes wired to the edit toggle, and both physician blocks.
 *
 * @param study - The study record supplying every displayed field.
 * @param toggleDisplayEditModal - Callback opening the study notes edit modal.
 * @returns An array of drawer sections, each with a header and its content rows.
 */
const formatDataStudy = (study: any, toggleDisplayEditModal: (...args: any[]) => void) => {
  const isIcorDevice = checkIcorDevice(study?.device);
  return [
    {
      id: study?.id,
      header: 'Study Information',
      content: [
        {
          label: 'Status',
          value: study?.status,
        },
        {
          label: 'Start Time',
          value: study?.start
            ? dateToStringWithDailyTimeZone({
                startStudyDate: study?.start,
                isoDate: study?.start,
                timezone: study?.timezone,
                facility: study?.facility,
              })
            : undefined,
        },
        {
          label: 'End Time',
          value: study?.stop
            ? dateToStringWithDailyTimeZone({
                startStudyDate: study?.start,
                isoDate: study?.stop,
                timezone: study?.timezone,
                facility: study?.facility,
              })
            : undefined,
        },
        {
          label: 'Study Type',
          value: study?.studyType,
          linkedStudies: study?.linkedStudies,
          followOnStudy: study?.info?.followOnStudy,
        },
        {
          label: 'Patient Return Visit',
          value: study?.reportDueDate
            ? dateToString(study?.reportDueDate, 'UTC+0', 'MM/DD/YYYY')
            : undefined,
        },
        {
          label: 'Device ID',
          value: (
            <div className="d-flex">
              {isIcorDevice ? (
                <div className="icor-device-container">
                  <span className="icor-device-id">{study?.device?.deviceId}</span>
                  <span className="icor-device-label">{DEVICE.ICOR}</span>
                </div>
              ) : (
                study?.device?.deviceId
              )}
              {study?.ecgDetail?.samplingRate === 1000 && (
                <>
                  <img id="high-sampling-rate-icon" className="ms-2" src={hsIcon} alt="" />
                  <UncontrolledTooltip
                    target="high-sampling-rate-icon"
                    className="custom-tooltip"
                    placement="bottom"
                    delay={{ show: 0, hide: 0 }}
                    hideArrow
                  >
                    {HIHT_SAMPLING_RATE_DEVICE_MESSAGE}
                  </UncontrolledTooltip>
                </>
              )}
              {isEndOfLife(study?.device?.firstStudyDate) && (
                <div className="eol-label ms-2">EOL</div>
              )}
            </div>
          ),
        },
        {
          label: 'Duration',
          value:
            study.status === STUDY_STATUS.STARTING
              ? moment
                  .duration(study?.initDuration, 'minutes')
                  .format('d[d] hh[h] mm[m]', { stopTrim: 'h' })
              : moment
                  .duration(getStudyDuration(study?.start, study?.stop), 'minutes')
                  .format('d[d] hh[h] mm[m]', { stopTrim: 'h' }),
        },
        {
          label: 'Facility',
          value: study?.facility?.name,
        },
        {
          label: 'Total artifact percentage',
          value: study?.artifact?.percentage,
          isArtifactPercentage: true,
        },
      ],
    },
    study?.linkedStudies?.length > 0 || study?.info?.followOnStudy
      ? {
          header: 'Follow-on study tracking',
          content: [
            {
              label: 'Study ID',
              value:
                study?.linkedStudies?.length > 0
                  ? zeroPad(study?.linkedStudies[0].friendlyId)
                  : zeroPad(study?.friendlyId),
            },
            {
              label: 'Study type',
              value:
                study?.linkedStudies?.length > 0
                  ? study?.linkedStudies[0].studyType
                  : study?.studyType,
            },
            {
              label: study?.linkedStudies?.length > 0 ? 'Study ID' : 'Study Duration',
              value:
                study?.linkedStudies?.length > 0
                  ? zeroPad(study?.linkedStudies[1].friendlyId)
                  : moment
                      .duration(study?.info?.followOnStudy.duration, 'minutes')
                      .format('d[d]', { stopTrim: 'h' }),
              isDisabled: !study?.linkedStudies || study?.linkedStudies.length === 0,
            },
            {
              label: 'Study type',
              value:
                study?.linkedStudies?.length > 0
                  ? study?.linkedStudies[1].studyType
                  : study?.info?.followOnStudy.studyType,
              isDisabled: !study?.linkedStudies || study?.linkedStudies.length === 0,
            },
          ],
          className: 'drawer-study-tracking',
        }
      : undefined,
    {
      header: 'Patient Information',
      content: [
        {
          label: 'Age',
          value: !_.isNil(study?.patientAge)
            ? study.patientAge > 70
              ? '70+'
              : study.patientAge
            : '--',
        },
        !isIcorDevice && {
          label: 'AFib detected',
          value: (study?.hasAFibCode ? true : study?.isAFibDetected) ? 'Activated' : 'Disabled',
        },
        {
          label: 'Pacemaker',
          value: study?.onboarding?.pacer || '--',
        },
        {
          label: 'ICD',
          value: study?.onboarding?.icd ? 'Yes' : 'No',
        },
        {
          label: 'Primary diagnosis code',
          value: study?.info?.diagnosis?.primary ? study.info.diagnosis.primary : '',
        },
        study?.info?.diagnosis?.second
          ? {
              label: 'Secondary diagnosis code',
              value: study.info.diagnosis.second,
            }
          : undefined,
      ],
    },
    {
      header: 'Study Notes',
      handleClickEdit: toggleDisplayEditModal,
      content: study?.studyNote,
    },
    {
      header: 'Physician amendments notes',
      content: study?.info?.diagnosis?.physiciansNote,
    },
    {
      header: 'Referring Physician',
      content: [
        {
          label: 'Name',
          value: `${study?.info?.referringPhysician?.firstName} ${study?.info?.referringPhysician?.lastName}`,
        },
        {
          label: 'Day Phone',
          value: study?.info?.referringPhysician?.contact?.phone1,
        },
        {
          label: 'After Hours Phone',
          value: study?.info?.referringPhysician?.contact?.nightPhone,
        },
        {
          label: 'Email',
          value: study?.info?.referringPhysician?.email,
        },
        {
          label: 'Fax Number',
          value: study?.info?.referringPhysician?.contact?.fax,
        },
      ],
    },
    {
      header: 'Interpreting Physician',
      content: [
        {
          label: 'Name',
          value: `${study?.info?.interprettingPhysician?.firstName} ${study?.info?.interprettingPhysician?.lastName}`,
        },
        {
          label: 'Day Phone',
          value: study?.info?.interprettingPhysician?.contact?.phone1,
        },
        {
          label: 'After Hours Phone',
          value: study?.info?.interprettingPhysician?.contact?.nightPhone,
        },
        {
          label: 'Email',
          value: study?.info?.interprettingPhysician?.email,
        },
        {
          label: 'Fax Number',
          value: study?.info?.interprettingPhysician?.contact?.fax,
        },
      ],
    },
  ];
};

export { formatDataStudy };
